import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  CreditCard, ChevronLeft, Receipt, Mail, Car, MapPin, User, Clock,
  CheckCircle, XCircle, AlertCircle, ExternalLink, Copy, RefreshCw,
  FileText, History, DollarSign, Building2
} from "lucide-react";
import axios from "axios";

const API = '/api';

/**
 * Payment Detail Page
 * Shows full payment timeline, receipt, Stripe details, email history, and audit
 */
export default function CommercialPaymentDetail() {
  const navigate = useNavigate();
  const { paymentId } = useParams();
  
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => { fetchPayment(); }, [paymentId]);

  const fetchPayment = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/kyle-rise/payments/${paymentId}`);
      setPayment(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle };
      case "pending": return { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock };
      case "failed": return { bg: "bg-red-100", text: "text-red-700", icon: XCircle };
      default: return { bg: "bg-gray-100", text: "text-gray-600", icon: AlertCircle };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Payment not found</p>
      </div>
    );
  }

  const statusStyle = getStatusColor(payment.status);
  const StatusIcon = statusStyle.icon;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-payment-detail">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/commercial/payments")} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl ${statusStyle.bg} flex items-center justify-center`}>
                <CreditCard className={`w-6 h-6 ${statusStyle.text}`} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{payment.payment_id}</h1>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                    {payment.status}
                  </span>
                  <span className="text-sm text-gray-500">{payment.amount_display}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              <Receipt className="w-4 h-4" /> View Receipt
            </button>
            <button className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              <Mail className="w-4 h-4" /> Resend Email
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Payment Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Payment Details</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Amount</span>
                <span className="text-lg font-bold text-green-600">{payment.amount_display}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Duration</span>
                <span className="font-medium">{payment.duration_hours} hours</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Status</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyle.bg} ${statusStyle.text}`}>{payment.status}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Created</span>
                <span className="text-sm">{new Date(payment.created_at).toLocaleString()}</span>
              </div>
              {payment.expires_at && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Expires</span>
                  <span className="text-sm">{new Date(payment.expires_at).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Vehicle Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Vehicle</h3>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-xl bg-yellow-100 flex items-center justify-center">
                <Car className="w-8 h-8 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{payment.reg_number}</p>
                <p className="text-sm text-gray-500">Registration Number</p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/admin/commercial/payments?search=${payment.reg_number}`)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <History className="w-4 h-4" /> View Payment History
            </button>
          </div>

          {/* Site & Operator */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Site & Operator</h3>
            <div className="space-y-3">
              {payment.site_id ? (
                <div 
                  onClick={() => navigate(`/admin/commercial/sites`)}
                  className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg cursor-pointer hover:bg-teal-100 transition-colors"
                >
                  <MapPin className="w-5 h-5 text-teal-600" />
                  <div>
                    <p className="text-sm font-medium text-teal-900">Site</p>
                    <p className="text-xs text-teal-700">{payment.site_id}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <p className="text-sm text-gray-500">No site assigned</p>
                </div>
              )}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Operator</p>
                  <p className="text-xs text-gray-500">System</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stripe Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Stripe Integration</h3>
          {payment.stripe_session_id ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Session ID</label>
                <div className="flex items-center gap-2">
                  <input type="text" value={payment.stripe_session_id} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono" />
                  <button onClick={() => copyToClipboard(payment.stripe_session_id, "session")} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                    {copied === "session" ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {payment.stripe_payment_intent && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Payment Intent</label>
                  <div className="flex items-center gap-2">
                    <input type="text" value={payment.stripe_payment_intent} readOnly className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono" />
                    <button onClick={() => copyToClipboard(payment.stripe_payment_intent, "intent")} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                      {copied === "intent" ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-800">Stripe integration pending</p>
                <p className="text-xs text-amber-600">Payment created locally, awaiting Stripe Checkout</p>
              </div>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Timeline</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium">Payment Created</p>
                <p className="text-xs text-gray-500">{new Date(payment.created_at).toLocaleString()}</p>
              </div>
            </div>
            {payment.paid_at && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">Payment Completed</p>
                  <p className="text-xs text-gray-500">{new Date(payment.paid_at).toLocaleString()}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Audit Trail Placeholder */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Audit Trail</h3>
            <button
              onClick={() => navigate(`/admin/commercial/audit?entity_id=${payment.payment_id}`)}
              className="text-sm text-blue-600 hover:underline"
            >
              View Full Audit
            </button>
          </div>
          <p className="text-sm text-gray-500">View full audit history in the Audit Centre</p>
        </div>
      </div>
    </div>
  );
}
